import { useEffect, useRef, useState } from "react";
import { getMeshStatus, subscribeMeshStatus } from "@/lib/lanMesh";
import { realtime } from "@/lib/realtime";
import { isInstalledAppEnvironment } from "@/lib/device";

/**
 * useConnectivity() — Session 30.8
 *
 * ONE derived connectivity model for the whole app so every surface
 * (pills, banners, refresh buttons, routing decisions) agrees on the
 * three very different situations the restaurant can be in:
 *
 *   lan:      "up"      ready WebRTC peers on the local Wi-Fi
 *             "pairing" peers announced, handshake in flight
 *             "lost"    we HAD peers this session and lost them
 *             "none"    no peers (single-device site / browser)
 *   internet: "up" | "down" | "unknown"
 *             cloud WS open  → up immediately
 *             /api/health OK → up
 *             navigator.onLine === false OR 2 consecutive probe
 *             failures → down
 *   wifi:     navigator.onLine (interface up, says nothing about the
 *             internet — this is what keeps LAN alive when the ISP dies)
 *
 * Derived `mode`:
 *   "online"    internet up (LAN may or may not be up)
 *   "lan-only"  internet down but LAN peers reachable  ← must keep working
 *   "offline"   no internet and no LAN
 *
 * `refreshAllowed` implements the operator rule: when the LAN
 * handshake has been LOST on an installed device, hide the Refresh
 * affordance so nobody "fixes" it by resetting the app; a controlled
 * reconnect is offered from the mesh pill instead.
 */

const PROBE_INTERVAL_MS = 15000;
const PROBE_TIMEOUT_MS = 8000;
const FAILS_TO_FLIP_DOWN = 2;

function _deriveLan(snap) {
  if (!snap || snap.status === "idle") return "none";
  if (snap.status === "lan" && (snap.peerCount || 0) > 0) return "up";
  if (snap.status === "lan") return "up"; // latched
  if (snap.lanLost) return "lost";
  if ((snap.announcedCount || 0) > 0) return "pairing";
  return "none";
}

export function useConnectivity() {
  const isNative = isInstalledAppEnvironment();
  const [lan, setLan] = useState(() => _deriveLan(safeMesh()));
  const [peerCount, setPeerCount] = useState(() => (safeMesh()?.peerCount || 0));
  const [wifi, setWifi] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine !== false : true));
  const [internet, setInternet] = useState(() => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return "down";
    return realtime && realtime._connected ? "up" : "unknown";
  });
  const failsRef = useRef(0);

  // LAN mesh
  useEffect(() => {
    const unsub = subscribeMeshStatus((snap) => {
      setLan(_deriveLan(snap));
      setPeerCount(snap?.peerCount || 0);
    });
    // The latch can flip without a peer event; poll lightly.
    const t = setInterval(() => {
      const snap = safeMesh();
      setLan(_deriveLan(snap));
      setPeerCount(snap?.peerCount || 0);
    }, 5000);
    return () => { try { unsub(); } catch (_) { /* noop */ } clearInterval(t); };
  }, []);

  // Wi-Fi interface + internet reachability
  useEffect(() => {
    let cancelled = false;
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

    const markUp = () => { failsRef.current = 0; if (!cancelled) setInternet("up"); };
    const markFail = () => {
      failsRef.current += 1;
      if (!cancelled && failsRef.current >= FAILS_TO_FLIP_DOWN) setInternet("down");
    };

    const probe = async () => {
      if (cancelled) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setWifi(false);
        setInternet("down");
        return;
      }
      setWifi(true);
      // Fast path: an open cloud socket proves the internet is up.
      if (realtime && realtime._connected && realtime.socket) { markUp(); return; }
      if (!backendUrl) return;
      try {
        const ctl = new AbortController();
        const timer = setTimeout(() => ctl.abort(), PROBE_TIMEOUT_MS);
        const r = await fetch(`${backendUrl}/api/health`, { method: "GET", cache: "no-store", signal: ctl.signal });
        clearTimeout(timer);
        if (r) markUp(); else markFail();
      } catch (_) {
        markFail();
      }
    };

    const onOnline = () => { setWifi(true); failsRef.current = 0; probe(); };
    const onOffline = () => { setWifi(false); setInternet("down"); };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const onVis = () => { if (document.visibilityState === "visible") probe(); };
    document.addEventListener("visibilitychange", onVis);

    probe();
    const id = setInterval(probe, PROBE_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const lanUp = lan === "up";
  const mode = internet === "up" ? "online" : (lanUp ? "lan-only" : (internet === "down" ? "offline" : "online"));
  const lanLost = lan === "lost";
  // Installed device that had LAN peers and lost them → hide Refresh.
  const refreshAllowed = !(isNative && lanLost);

  return { isNative, lan, lanUp, lanLost, peerCount, wifi, internet, mode, refreshAllowed };
}

function safeMesh() {
  try { return getMeshStatus(); } catch (_) { return null; }
}
